import React, { useState, useEffect } from 'react';
import { Calendar } from '../components/ui/calendar';
import { fr as frLocale, enGB } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useLanguage, routeMapping } from '../context/LanguageContext';
import { translations } from '../data/translations';
import SEOHead from '../components/SEOHead';

const ContactBilingual = () => {
  const { language, t } = useLanguage();
  const contact = translations.contact;
  const nav = translations.nav;
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [takenTimes, setTakenTimes] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);

  useEffect(() => {
    if (!showScheduler) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/blocked-days`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setBlockedDays)
      .catch(() => {});
  }, [showScheduler]);
  const [booking, setBooking] = useState({ fullName: '', email: '', message: '', format: 'video', phone: '' });

  const toIsoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const loadAvailability = async (d) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/availability?date=${toIsoDate(d)}`);
    if (res.ok) setTakenTimes((await res.json()).taken);
  };

  const handleDateSelect = (d) => {
    setSelectedDate(d);
    setSelectedTime(null);
    setTakenTimes([]);
    if (d) loadAvailability(d);
  };

  const availableTimes = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          company: formData.company || null,
          phone: formData.phone || null,
          message: formData.message,
          language: language
        })
      });
      
      if (!response.ok) throw new Error('Failed to send');
      
      toast.success(language === 'en' ? 'Message sent successfully' : 'Message envoyé avec succès');
      setFormData({ fullName: '', email: '', company: '', phone: '', message: '' });
    } catch (err) {
      toast.error(language === 'en' ? 'An error occurred. Please try again.' : 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error(language === 'en' ? 'Please select both date and time' : 'Veuillez sélectionner une date et une heure');
      return;
    }
    
    if (!booking.fullName.trim() || !booking.email.trim()) {
      toast.error(t(contact.fillDetails));
      return;
    }
    if (booking.format === 'call' && !booking.phone.trim()) {
      toast.error(t(contact.phoneRequired));
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: booking.fullName,
          email: booking.email,
          message: booking.message || null,
          format: booking.format,
          phone: booking.phone || null,
          date: toIsoDate(selectedDate),
          time: selectedTime,
          language
        })
      });
      if (res.status === 409) {
        toast.error(t(contact.slotTaken));
        setSelectedTime(null);
        loadAvailability(selectedDate);
        return;
      }
      if (!res.ok) throw new Error('failed');
      toast.success(t(contact.bookingSuccess));
      setSelectedDate(null);
      setSelectedTime(null);
      setBooking({ fullName: '', email: '', message: '', format: 'video', phone: '' });
      setShowScheduler(false);
    } catch {
      toast.error(language === 'en' ? 'An error occurred. Please try again.' : 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEOHead 
        title={t(contact.seoTitle)}
        description={t(contact.seoDescription)}
      />
      
      {/* Hero Section */}
      <section className="section-padding-large" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
              {t(contact.heroSubtitle)}
            </p>
            <h1 className="hero-large" style={{ marginBottom: '32px' }}>
              {t(contact.heroTitle)}
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {t(contact.heroDescription)}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
            gap: '80px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Form */}
            <div>
              {!showScheduler ? (
                <>
                  <h2 className="heading-2" style={{ marginBottom: '32px' }}>
                    {t(contact.formTitle)}
                  </h2>
                  
                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gap: '24px' }}>
                      <div>
                        <Label htmlFor="fullName" className="body-small" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-secondary)'
                        }}>
                          {t(contact.fullName)}
                        </Label>
                        <Input
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          style={{
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-light)',
                            padding: '16px',
                            fontSize: '16px'
                          }}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email" className="body-small" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-secondary)'
                        }}>
                          {t(contact.email)}
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          style={{
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-light)',
                            padding: '16px',
                            fontSize: '16px'
                          }}
                        />
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                        <div>
                          <Label htmlFor="company" className="body-small" style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            color: 'var(--text-secondary)'
                          }}>
                            {t(contact.company)}
                          </Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            style={{
                              background: 'var(--bg-primary)',
                              border: '1px solid var(--border-light)',
                              padding: '16px',
                              fontSize: '16px'
                            }}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="phone" className="body-small" style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            color: 'var(--text-secondary)'
                          }}>
                            {t(contact.phone)}
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            style={{
                              background: 'var(--bg-primary)',
                              border: '1px solid var(--border-light)',
                              padding: '16px',
                              fontSize: '16px'
                            }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="message" className="body-small" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-secondary)'
                        }}>
                          {t(contact.message)}
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows={6}
                          placeholder={t(contact.messagePlaceholder)}
                          style={{
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-light)',
                            padding: '16px',
                            fontSize: '16px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={isSubmitting}
                        style={{ width: '100%' }}
                      >
                        {isSubmitting ? '...' : t(contact.sendMessage)}
                      </Button>
                    </div>
                  </form>
                  
                  <div style={{ marginTop: '32px', textAlign: 'center' }}>
                    <p className="body-small" style={{ 
                      color: 'var(--text-light)',
                      marginBottom: '16px'
                    }}>
                      {t(contact.orSchedule)}
                    </p>
                    <button
                      data-testid="open-scheduler-button" onClick={() => setShowScheduler(true)}
                      className="btn-secondary"
                    >
                      {t(contact.scheduleTitle)}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="heading-2" style={{ marginBottom: '16px' }}>
                    {t(contact.scheduleTitle)}
                  </h2>
                  <p className="body-regular" style={{ 
                    marginBottom: '32px',
                    color: 'var(--text-secondary)'
                  }}>
                    {t(contact.scheduleDescription)}
                  </p>
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: '40px',
                    flexWrap: 'wrap'
                  }}>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      locale={language === 'fr' ? frLocale : enGB}
                      weekStartsOn={1}
                      disabled={(date) => blockedDays.includes(toIsoDate(date)) || toIsoDate(date) <= new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' }) || date.getDay() === 0 || date.getDay() === 6}
                      className="rounded-md border"
                      data-testid="booking-calendar"
                    />
                    
                    {selectedDate && (
                      <div>
                        <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                          {t(contact.selectTime)}
                        </h3>
                        <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '16px' }}>{t(contact.parisTime)}</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {availableTimes.map((time) => {
                            const taken = takenTimes.includes(time);
                            return (
                            <button
                              key={time}
                              data-testid={`booking-time-${time.replace(':', '')}`}
                              disabled={taken}
                              onClick={() => setSelectedTime(time)}
                              style={{
                                padding: '12px 20px',
                                background: selectedTime === time ? 'var(--interactive-base)' : 'var(--bg-primary)',
                                color: selectedTime === time ? 'var(--bg-primary)' : 'var(--text-primary)',
                                border: '1px solid var(--border-light)',
                                cursor: taken ? 'not-allowed' : 'pointer',
                                opacity: taken ? 0.35 : 1,
                                textDecoration: taken ? 'line-through' : 'none',
                                fontSize: '14px'
                              }}
                            >
                              {time}
                            </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedTime && (
                    <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '520px' }}>
                      <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{t(contact.formatTitle)}</p>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }} role="radiogroup">
                        {[['call', contact.formatCall], ['video', contact.formatVideo], ['meeting', contact.formatMeeting]].map(([key, label]) => (
                          <button key={key} type="button" role="radio" aria-checked={booking.format === key} data-testid={`booking-format-${key}`}
                            onClick={() => setBooking({ ...booking, format: key })}
                            style={{ padding: '10px 16px', fontSize: '14px', cursor: 'pointer', border: '1px solid var(--border-light)', transition: 'background-color 0.2s ease, color 0.2s ease',
                              background: booking.format === key ? 'var(--interactive-base)' : 'var(--bg-primary)', color: booking.format === key ? 'var(--bg-primary)' : 'var(--text-primary)' }}>
                            {t(label)}
                          </button>
                        ))}
                      </div>
                      {booking.format === 'call' && (
                        <Input data-testid="booking-phone-input" type="tel" required aria-label={t(contact.bookingPhone)} placeholder={t(contact.bookingPhone)} value={booking.phone}
                          onChange={(e) => setBooking({ ...booking, phone: e.target.value })}
                          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)', padding: '16px', fontSize: '16px', height: 'auto' }} />
                      )}
                      <Input data-testid="booking-name-input" required aria-label={t(contact.fullName)} placeholder={t(contact.fullName)} value={booking.fullName}
                        onChange={(e) => setBooking({ ...booking, fullName: e.target.value })}
                        style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)', padding: '16px', fontSize: '16px', height: 'auto' }} />
                      <Input data-testid="booking-email-input" type="email" required aria-label={t(contact.email)} placeholder={t(contact.email)} value={booking.email}
                        onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                        style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)', padding: '16px', fontSize: '16px', height: 'auto' }} />
                      <textarea data-testid="booking-message-input" rows={3} placeholder={t(contact.bookingMessage)} value={booking.message}
                        onChange={(e) => setBooking({ ...booking, message: e.target.value })}
                        style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)', padding: '16px', fontSize: '16px', resize: 'vertical' }} />
                    </div>
                  )}
                  
                  <div style={{ marginTop: '32px', display: 'flex', gap: '16px' }}>
                    <Button
                      data-testid="booking-confirm-button"
                      onClick={handleScheduleSubmit}
                      className="btn-primary"
                      disabled={!selectedDate || !selectedTime || isSubmitting}
                    >
                      {t(contact.confirmSchedule)}
                    </Button>
                    <button
                      data-testid="booking-back-button" onClick={() => setShowScheduler(false)}
                      className="btn-secondary"
                    >
                      {t(contact.backToForm)}
                    </button>
                  </div>
                </>
              )}
            </div>
            
            {/* Contact Info */}
            <div>
              <div style={{ marginBottom: '48px' }}>
                <h3 className="heading-3" style={{ 
                  marginBottom: '24px',
                  color: 'var(--gold-accent)'
                }}>
                  {t(contact.directContactTitle)}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <a 
                    href="mailto:nidale.barret@fenicis.com"
                    className="body-regular"
                    style={{ 
                      color: 'var(--text-primary)',
                      textDecoration: 'none'
                    }}
                  >
                    nidale.barret@fenicis.com
                  </a>
                  <a 
                    href="tel:+33625394939"
                    className="body-regular"
                    style={{ 
                      color: 'var(--text-primary)',
                      textDecoration: 'none'
                    }}
                  >
                    +33 6 25 39 49 39
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="heading-3" style={{ 
                  marginBottom: '24px',
                  color: 'var(--gold-accent)'
                }}>
                  {t(contact.locationTitle)}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {t(contact.location)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confidentiality Note */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
              {t(contact.confidentialityNote)}
            </p>
          </div>
          <div style={{ marginTop: '64px' }}>
            <NewsletterSignup source="contact" />
          </div>
        </div>
      </section>

      {/* Micro-SEO Block */}
      <section style={{ padding: '40px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <p style={{ 
            fontSize: '12px',
            lineHeight: 1.7,
            color: 'var(--text-light)',
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            {t(contact.seoBlock)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactBilingual;
